import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, memo } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { RouteGuard } from "@/components/RouteGuard";
import { AuthProvider } from "@/contexts/AuthContext";
import { SuperAdminPreviewProvider } from "@/contexts/SuperAdminPreviewContext";
import { SuperAdminPreviewBanner } from "@/components/SuperAdminPreviewBanner";
import { CookieConsentBanner } from "@/components/gdpr/CookieConsentBanner";
import { DataConsentGate } from "@/components/gdpr/DataConsentGate";
import { PWAInstallPrompt } from "@/components/pwa/PWAInstallPrompt";

// Lazy load pages for better performance
const Index = lazyWithRetry(() => import("./pages/Index"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const SectorDashboard = lazyWithRetry(() => import("./pages/SectorDashboard"));
const NewAppointment = lazyWithRetry(() => import("./pages/NewAppointment"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const AppointmentsPending = lazyWithRetry(() => import("./pages/AppointmentsPending"));
const AppointmentsAccepted = lazyWithRetry(() => import("./pages/AppointmentsAccepted"));
const AppointmentsRejected = lazyWithRetry(() => import("./pages/AppointmentsRejected"));
const AppointmentsCancelled = lazyWithRetry(() => import("./pages/AppointmentsCancelled"));
const StoresDirectory = lazyWithRetry(() => import("./pages/StoresDirectory"));
const Availability = lazyWithRetry(() => import("./pages/Availability"));
const Billing = lazyWithRetry(() => import("./pages/Billing"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const StoreAdminDashboard = lazyWithRetry(() => import("./pages/StoreAdminDashboard"));
const SupplierAdminDashboard = lazyWithRetry(() => import("./pages/SupplierAdminDashboard"));
const StoreTeamManagement = lazyWithRetry(() => import("./pages/StoreTeamManagement"));
const SupplierTeamManagement = lazyWithRetry(() => import("./pages/SupplierTeamManagement"));
const StoreAdminVisits = lazyWithRetry(() => import("./pages/StoreAdminVisits"));
const SupplierAdminVisits = lazyWithRetry(() => import("./pages/SupplierAdminVisits"));
const StoreSettings = lazyWithRetry(() => import("./pages/StoreSettings"));
const StoreAdminDepartments = lazyWithRetry(() => import("./pages/StoreAdminDepartments"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Permissions = lazyWithRetry(() => import("./pages/Permissions"));
const AdminAnalytics = lazyWithRetry(() => import("./pages/AdminAnalytics"));
const AdminAnalyses = lazyWithRetry(() => import("./pages/AdminAnalyses"));
const AdminStoreDetail = lazyWithRetry(() => import("./pages/AdminStoreDetail"));
const AdminStoreStudio = lazyWithRetry(() => import("./pages/AdminStoreStudio"));
const AdminEnterpriseDetail = lazyWithRetry(() => import("./pages/AdminEnterpriseDetail"));
const AdminContactRequests = lazyWithRetry(() => import("./pages/AdminContactRequests"));
const SetPassword = lazyWithRetry(() => import("./pages/SetPassword"));
const StoreAdminSuppliers = lazyWithRetry(() => import("./pages/StoreAdminSuppliers"));
const SupplierActivityProfile = lazyWithRetry(() => import("./pages/SupplierActivityProfile"));
const BlacklistAnalytics = lazyWithRetry(() => import("./pages/BlacklistAnalytics"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications"));
const AdminCronRuns = lazyWithRetry(() => import("./pages/AdminCronRuns"));
const AdminAccountAudit = lazyWithRetry(() => import("./pages/AdminAccountAudit"));
const AdminSupplierDiagnostic = lazyWithRetry(() => import("./pages/AdminSupplierDiagnostic"));
const KamDashboard = lazyWithRetry(() => import("./pages/KamDashboard"));
const QrDashboard = lazyWithRetry(() => import("./pages/QrDashboard"));

// Static public pages
const MentionsLegales = lazyWithRetry(() => import("./pages/legal/MentionsLegales"));
const Confidentialite = lazyWithRetry(() => import("./pages/legal/Confidentialite"));
const Contact = lazyWithRetry(() => import("./pages/legal/Contact"));
const CGU = lazyWithRetry(() => import("./pages/legal/CGU"));
const CGV = lazyWithRetry(() => import("./pages/legal/CGV"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/legal/PrivacyPolicy"));
const CookiesPolicy = lazyWithRetry(() => import("./pages/legal/CookiesPolicy"));
const RgpdCompliance = lazyWithRetry(() => import("./pages/legal/RgpdCompliance"));
const LandingMagasins = lazyWithRetry(() => import("./pages/LandingMagasins"));
const LandingFournisseurs = lazyWithRetry(() => import("./pages/LandingFournisseurs"));
const OnePageMagasin = lazyWithRetry(() => import("./pages/OnePageMagasin"));
const SupplierCheckin = lazyWithRetry(() => import("./pages/SupplierCheckin"));
const StoreQRCode = lazyWithRetry(() => import("./pages/StoreQRCode"));
const ScanPage = lazyWithRetry(() => import("./pages/ScanPage"));
const SupplierScanRecognition = lazyWithRetry(() => import("./pages/SupplierScanRecognition"));
const ScanChooser = lazyWithRetry(() => import("./pages/ScanChooser"));
const ScanReceipt = lazyWithRetry(() => import("./pages/ScanReceipt"));
const ExternalVisitorCheckin = lazyWithRetry(() => import("./pages/ExternalVisitorCheckin"));
const PresenceAnalytics = lazyWithRetry(() => import("./pages/PresenceAnalytics"));
const AdminCentrales = lazyWithRetry(() => import("./pages/AdminCentrales"));
const StudioCentraleDraft = lazyWithRetry(() => import("./pages/StudioCentraleDraft"));
const AdminJobFunctions = lazyWithRetry(() => import("./pages/AdminJobFunctions"));
const AdminSectorsCatalog = lazyWithRetry(() => import("./pages/AdminSectorsCatalog"));
const AdminStoreMapping = lazyWithRetry(() => import("./pages/AdminStoreMapping"));
const AdminStoreSettings = lazyWithRetry(() => import("./pages/AdminStoreSettings"));
const AdminSupplierVisibilityShadow = lazyWithRetry(() => import("./pages/AdminSupplierVisibilityShadow"));
const AdminSupplierSettings = lazyWithRetry(() => import("./pages/AdminSupplierSettings"));
const AdminV2Readiness = lazyWithRetry(() => import("./pages/AdminV2Readiness"));
const AdminSupportTickets = lazyWithRetry(() => import("./pages/AdminSupportTickets"));
const CentraleDashboard = lazyWithRetry(() => import("./pages/CentraleDashboard"));
const CentraleStoreDetail = lazyWithRetry(() => import("./pages/CentraleStoreDetail"));
const CentraleQRCode = lazyWithRetry(() => import("./pages/CentraleQRCode"));
const CentraleVisitorCheckin = lazyWithRetry(() => import("./pages/CentraleVisitorCheckin"));
const CentraleAppointments = lazyWithRetry(() => import("./pages/CentraleAppointments"));
const CentraleGTManagement = lazyWithRetry(() => import("./pages/CentraleGTManagement"));
const CentraleTasks = lazyWithRetry(() => import("./pages/CentraleTasks"));
const DailyLogs = lazyWithRetry(() => import("./pages/DailyLogs"));
const RecurringTasks = lazyWithRetry(() => import("./pages/RecurringTasks"));
const PresenceLogs = lazyWithRetry(() => import("./pages/PresenceLogs"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const AccessTokenResolver = lazyWithRetry(() => import("./pages/AccessTokenResolver"));
const AccessSlugResolver = lazyWithRetry(() => import("./pages/AccessSlugResolver"));
const CentralScan = lazyWithRetry(() => import("./pages/CentralScan"));
const CentraleCheckPoints = lazyWithRetry(() => import("./pages/CentraleCheckPoints"));

// SuperAdmin V2 (Lot 1 — skeleton only, additive, /admin reste intact)
const AppShellAdminV2 = lazyWithRetry(() => import("./components/admin-v2/AppShellAdminV2"));
const AdminV2Cockpit = lazyWithRetry(() => import("./pages/admin-v2/AdminV2Cockpit"));
const AdminV2Placeholder = lazyWithRetry(() => import("./pages/admin-v2/AdminV2Placeholder"));

// STRICT ROLE SEPARATION: Define allowed roles for each route category
// Governance routes: Only 'admin' role - manages the SYSTEM
// Business routes: Business roles EXCEPT 'admin' and 'admin_magasin' - manages BUSINESS data
// Admin Magasin has its own dedicated dashboard - NOT the same as chef_rayon
// NOTE: 'magasin' is NOT a user role - it's an organizational entity (stores table)
// Only admin_magasin and chef_rayon are actual user roles on the store side
const BUSINESS_ROLES = ["fournisseur", "chef_rayon", "chef_secteur_magasin", "collaborateur_magasin", "admin_fournisseur", "kam"];
const SUPPLIER_ROLES = ["fournisseur", "admin_fournisseur", "kam"];
const SUPPLIER_CREATE_ROLES = ["fournisseur", "kam", "supplier_guest"]; // Commercials AND KAM can create appointments
const GOVERNANCE_ROLES = ["admin"];

// Configure React Query with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Données considérées fraîches 5 min → pas de refetch inutile
      staleTime: 1000 * 60 * 5,
      // Cache conservé 30 min après inactivité
      gcTime: 1000 * 60 * 30,
      // Désactivé : Realtime (postgres_changes) gère déjà l'invalidation côté serveur.
      // Évite les rafales de requêtes à chaque retour d'onglet et les flickers.
      refetchOnWindowFocus: false,
      // Évite un refetch automatique à la reconnexion réseau (déclenché par Realtime au besoin)
      refetchOnReconnect: "always",
      retry: 1,
    },
  },
});

// Memoized loading fallback component
const PageLoader = memo(() => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
));

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
        <SuperAdminPreviewProvider>
        <SuperAdminPreviewBanner />
        <DataConsentGate>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/confidentialite" element={<Confidentialite />} />
            <Route path="/cgu" element={<CGU />} />
            <Route path="/cgv" element={<CGV />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/cookies" element={<CookiesPolicy />} />
            <Route path="/legal/rgpd" element={<RgpdCompliance />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/magasins" element={<LandingMagasins />} />
            <Route path="/fournisseurs" element={<LandingFournisseurs />} />
            <Route path="/onepage-magasin" element={<OnePageMagasin />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/checkin/:storeId" element={
              <RouteGuard allowedRoles={SUPPLIER_ROLES} redirectTo="/auth">
                <SupplierCheckin />
              </RouteGuard>
            } />

            {/* ===== BUSINESS ROUTES - Strictly forbidden for 'admin' role ===== */}
            {/* Dashboard - Business users only */}
            <Route path="/dashboard" element={
              <RouteGuard allowedRoles={BUSINESS_ROLES} redirectTo="/auth">
                <Dashboard />
              </RouteGuard>
            } />

            {/* Sector Dashboard - Responsables sur-famille / Chef de secteur */}
            <Route path="/sector-dashboard" element={
              <RouteGuard allowedRoles={["chef_rayon", "chef_secteur_magasin", "admin_magasin"]} redirectTo="/dashboard">
                <SectorDashboard />
              </RouteGuard>
            } />
            
            {/* Appointments - Business users only */}
            <Route path="/appointments/new" element={
              <RouteGuard allowedRoles={SUPPLIER_CREATE_ROLES} redirectTo="/dashboard">
                <NewAppointment />
              </RouteGuard>
            } />
            <Route path="/appointments/pending" element={
              <RouteGuard allowedRoles={BUSINESS_ROLES} redirectTo="/dashboard">
                <AppointmentsPending />
              </RouteGuard>
            } />
            <Route path="/appointments/accepted" element={
              <RouteGuard allowedRoles={BUSINESS_ROLES} redirectTo="/dashboard">
                <AppointmentsAccepted />
              </RouteGuard>
            } />
            <Route path="/appointments/rejected" element={
              <RouteGuard allowedRoles={BUSINESS_ROLES} redirectTo="/dashboard">
                <AppointmentsRejected />
              </RouteGuard>
            } />
            <Route path="/appointments/cancelled" element={
              <RouteGuard allowedRoles={BUSINESS_ROLES} redirectTo="/dashboard">
                <AppointmentsCancelled />
              </RouteGuard>
            } />
            
            {/* Profile - Tous les rôles authentifiés (y compris Chef de secteur, Collaborateur magasin, Admin gouvernance) */}
            <Route path="/profile" element={
              <RouteGuard
                allowedRoles={[
                  "fournisseur",
                  "supplier_guest",
                  "chef_rayon",
                  "chef_secteur_magasin",
                  "collaborateur_magasin",
                  "admin_fournisseur",
                  "admin_magasin",
                  "admin_centrale",
                  "admin",
                  "kam",
                ]}
                redirectTo="/auth"
              >
                <Profile />
              </RouteGuard>
            } />
            
            {/* Stores - Suppliers (commercials + admins) and admin_magasin can view */}
            <Route path="/stores" element={
              <RouteGuard allowedRoles={["fournisseur", "admin_fournisseur", "admin_magasin"]} redirectTo="/dashboard">
                <StoresDirectory />
              </RouteGuard>
            } />
            
            {/* Availability - Chef de rayon, Admin magasin, collaborateurs magasin (services support),
                + admin_centrale uniquement si rattaché à un GT/service HQ via PDA active (garde-fou côté composant). */}
            <Route path="/availability" element={
              <RouteGuard allowedRoles={["chef_rayon", "admin_magasin", "collaborateur_magasin", "chef_secteur_magasin", "admin_centrale"]} redirectTo="/dashboard">
                <Availability />
              </RouteGuard>
            } />
            
            {/* Billing - ONLY admin_fournisseur (Stores are FREE, SuperAdmin is governance only) */}
            <Route path="/billing" element={
              <RouteGuard allowedRoles={["admin_fournisseur"]} redirectTo="/dashboard">
                <Billing />
              </RouteGuard>
            } />
            
            {/* ===== GOVERNANCE ROUTES - Strictly for 'admin' role only ===== */}
            <Route path="/admin" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminDashboard />
              </RouteGuard>
            } />
            <Route path="/admin/dashboard" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <Navigate to="/admin" replace />
              </RouteGuard>
            } />
            <Route path="/admin/analytics" element={
              <RouteGuard allowedRoles={["admin", "admin_magasin", "admin_fournisseur"]} redirectTo="/auth">
                <AdminAnalytics />
              </RouteGuard>
            } />
            <Route path="/admin/analyses" element={
              <RouteGuard allowedRoles={["admin", "admin_magasin", "admin_fournisseur"]} redirectTo="/auth">
                <AdminAnalyses />
              </RouteGuard>
            } />
            <Route path="/analyses" element={
              <RouteGuard allowedRoles={["admin_magasin", "admin_fournisseur"]} redirectTo="/auth">
                <AdminAnalyses />
              </RouteGuard>
            } />
            <Route path="/admin/magasins/:id" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminStoreDetail />
              </RouteGuard>
            } />
            <Route path="/admin/magasins/:id/studio" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminStoreStudio />
              </RouteGuard>
            } />
            <Route path="/admin/fournisseurs/:id" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminEnterpriseDetail />
              </RouteGuard>
            } />
            <Route path="/admin/demandes-contact" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminContactRequests />
              </RouteGuard>
            } />
            <Route path="/admin/centrales" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminCentrales />
              </RouteGuard>
            } />
            <Route path="/admin/centrales/studio/:centraleId" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <StudioCentraleDraft />
              </RouteGuard>
            } />
            <Route path="/admin/audit-comptes" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminAccountAudit />
              </RouteGuard>
            } />
            <Route path="/admin/diagnostic-fournisseur/:userId" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminSupplierDiagnostic />
              </RouteGuard>
            } />
            <Route path="/admin/fonctions-metier" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminJobFunctions />
              </RouteGuard>
            } />
            <Route path="/admin/sectors-catalog" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminSectorsCatalog />
              </RouteGuard>
            } />
            <Route path="/admin/store-mapping" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminStoreMapping />
              </RouteGuard>
            } />
            <Route path="/admin/store-settings" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminStoreSettings />
              </RouteGuard>
            } />
            <Route path="/admin/supplier-visibility-shadow" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminSupplierVisibilityShadow />
              </RouteGuard>
            } />
            <Route path="/admin/supplier-settings" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminSupplierSettings />
              </RouteGuard>
            } />
            <Route path="/admin/v2-readiness" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminV2Readiness />
              </RouteGuard>
            } />
            <Route path="/admin/support-tickets" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminSupportTickets />
              </RouteGuard>
            } />
            
            {/* ===== BUSINESS ADMIN ROUTES ===== */}
            <Route path="/magasin/admin/dashboard" element={
              <RouteGuard allowedRoles={["admin_magasin"]} redirectTo="/dashboard">
                <StoreAdminDashboard />
              </RouteGuard>
            } />
            <Route path="/magasin/admin/team" element={
              <RouteGuard allowedRoles={["admin_magasin"]} redirectTo="/dashboard">
                <StoreTeamManagement />
              </RouteGuard>
            } />
            <Route path="/magasin/admin/visites" element={
              <RouteGuard allowedRoles={["admin_magasin"]} redirectTo="/dashboard">
                <StoreAdminVisits />
              </RouteGuard>
            } />
            <Route path="/magasin/admin/parametres" element={
              <RouteGuard allowedRoles={["admin_magasin"]} redirectTo="/dashboard">
                <StoreSettings />
              </RouteGuard>
            } />
            <Route path="/magasin/admin/rayons" element={
              <RouteGuard allowedRoles={["admin_magasin"]} redirectTo="/dashboard">
                <StoreAdminDepartments />
              </RouteGuard>
            } />
            <Route path="/magasin/admin/fournisseurs" element={
              <RouteGuard allowedRoles={["admin_magasin", "chef_rayon"]} redirectTo="/dashboard">
                <StoreAdminSuppliers />
              </RouteGuard>
            } />
            <Route path="/magasin/analytics/blacklist" element={
              <RouteGuard allowedRoles={["admin_magasin", "chef_rayon"]} redirectTo="/dashboard">
                <BlacklistAnalytics />
              </RouteGuard>
            } />
            <Route path="/magasin/admin/fournisseurs/:supplierId" element={
              <RouteGuard allowedRoles={["admin_magasin", "chef_rayon"]} redirectTo="/dashboard">
                <SupplierActivityProfile />
              </RouteGuard>
            } />
            <Route path="/fournisseur/admin/dashboard" element={
              <RouteGuard allowedRoles={["admin_fournisseur"]} redirectTo="/dashboard">
                <SupplierAdminDashboard />
              </RouteGuard>
            } />
            <Route path="/fournisseur/admin/team" element={
              <RouteGuard allowedRoles={["admin_fournisseur"]} redirectTo="/dashboard">
                <SupplierTeamManagement />
              </RouteGuard>
            } />
            <Route path="/fournisseur/admin/visites" element={
              <RouteGuard allowedRoles={["admin_fournisseur"]} redirectTo="/dashboard">
                <SupplierAdminVisits />
              </RouteGuard>
            } />
            
            
            {/* Internal reference page - accessible to all authenticated roles */}
            <Route path="/permissions" element={
              <RouteGuard allowedRoles={["admin", "admin_magasin", "admin_fournisseur", "admin_centrale", "chef_rayon", "fournisseur", "kam"]} redirectTo="/auth">
                <Permissions />
              </RouteGuard>
            } />

            {/* QR Code management - admin_magasin + superadmin */}
            <Route path="/magasin/admin/qrcode" element={
              <RouteGuard allowedRoles={["admin_magasin", "admin"]} redirectTo="/dashboard">
                <StoreQRCode />
              </RouteGuard>
            } />

            {/* Presence Analytics */}
            <Route path="/presence-analytics" element={
              <RouteGuard allowedRoles={["admin_magasin", "admin", "chef_rayon", "chef_secteur_magasin", "collaborateur_magasin", "admin_centrale"]} redirectTo="/dashboard">
                <PresenceAnalytics />
              </RouteGuard>
            } />
            <Route path="/magasin/admin/presence" element={
              <RouteGuard allowedRoles={["admin_magasin", "admin"]} redirectTo="/dashboard">
                <PresenceAnalytics />
              </RouteGuard>
            } />

            {/* Main courante (daily logs) */}
            <Route path="/main-courante" element={
              <RouteGuard allowedRoles={["chef_rayon", "admin_magasin", "admin_centrale", "admin"]} redirectTo="/dashboard">
                <DailyLogs />
              </RouteGuard>
            } />

            {/* Tâches récurrentes */}
            <Route path="/taches" element={
              <RouteGuard allowedRoles={["chef_rayon", "admin_magasin", "admin", "admin_centrale"]} redirectTo="/dashboard">
                <RecurringTasks />
              </RouteGuard>
            } />

            {/* Registre de présence unifié */}
            <Route path="/presence" element={
              <RouteGuard allowedRoles={["chef_rayon", "collaborateur_magasin", "admin_magasin", "admin_centrale", "admin"]} redirectTo="/dashboard">
                <PresenceLogs />
              </RouteGuard>
            } />

            {/* Centrale Dashboard */}
            <Route path="/centrale/dashboard" element={
              <RouteGuard allowedRoles={["admin_centrale", "chef_rayon"]} redirectTo="/auth">
                <CentraleDashboard />
              </RouteGuard>
            } />
            {/* Centrale Mes RDV — onglet principal RDV */}
            <Route path="/centrale/rdv" element={
              <RouteGuard allowedRoles={["admin_centrale", "chef_rayon"]} redirectTo="/auth">
                <CentraleAppointments />
              </RouteGuard>
            } />
            <Route path="/centrale/magasin/:id" element={
              <RouteGuard allowedRoles={["admin_centrale", "admin"]} redirectTo="/auth">
                <CentraleStoreDetail />
              </RouteGuard>
            } />
            <Route path="/centrale/qrcode" element={
              <RouteGuard allowedRoles={["admin_centrale", "admin"]} redirectTo="/auth">
                <CentraleQRCode />
              </RouteGuard>
            } />
            <Route path="/centrale/gt" element={
              <RouteGuard allowedRoles={["admin_centrale"]} redirectTo="/auth">
                <CentraleGTManagement />
              </RouteGuard>
            } />
            <Route path="/centrale/taches" element={
              <RouteGuard allowedRoles={["admin_centrale"]} redirectTo="/auth">
                <CentraleTasks />
              </RouteGuard>
            } />

            {/* Centrale visitor check-in — NO auth required */}
            <Route path="/centrale-visitor-checkin" element={<CentraleVisitorCheckin />} />

            {/* Scan page — suppliers go through existing flow, others redirected to visitor form */}
            <Route path="/scan" element={<ScanPage />} />

            {/* QR/NFC tokenisé — résout puis redirige vers /scan (flow inchangé) */}
            <Route path="/s/:token" element={<AccessTokenResolver />} />

            {/* Fallback URL lisible (Bug #3) — alias humain → même flow que le QR */}
            <Route path="/go/:slug" element={<AccessSlugResolver />} />

            {/* Supplier scan recognition (anonymous, no session) */}
            <Route path="/scan/supplier" element={<SupplierScanRecognition />} />

            {/* Neutral chooser when scanning without session/cache */}
            <Route path="/scan/choose" element={<ScanChooser />} />

            {/* Reçu inerte après scan QR (intervenant sans dashboard).
                Lecture seule, refresh / back / bfcache sans risque. */}
            <Route path="/scan/receipt" element={<ScanReceipt />} />

            {/* Centrale multi-point check-in/out — PUBLIC (anonymous-friendly).
                La page gère elle-même le branchement connecté / non-connecté. */}
            <Route path="/central-scan" element={<CentralScan />} />

            {/* Centrale: gestion des points de scan (admin_centrale + SuperAdmin) */}
            <Route path="/centrale/check-points" element={
              <RouteGuard allowedRoles={["admin", "admin_centrale"]} redirectTo="/auth">
                <CentraleCheckPoints />
              </RouteGuard>
            } />

            {/* External visitor check-in — NO auth required */}
            <Route path="/visitor-checkin" element={<ExternalVisitorCheckin />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />

            {/* Notifications center (all authenticated users) */}
            <Route path="/notifications" element={
              <RouteGuard allowedRoles={["admin", "admin_magasin", "admin_fournisseur", "admin_centrale", "chef_rayon", "fournisseur", "kam", "supplier_guest"]} redirectTo="/auth">
                <Notifications />
              </RouteGuard>
            } />

            {/* Cron observability — SuperAdmin only */}
            <Route path="/admin/cron-runs" element={
              <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                <AdminCronRuns />
              </RouteGuard>
            } />

            {/* KAM dedicated dashboard — Centrale HQ universe */}
            <Route path="/kam/dashboard" element={
              <RouteGuard allowedRoles={["kam"]} redirectTo="/auth">
                <KamDashboard />
              </RouteGuard>
            } />

            {/* QR-created supplier minimal dashboard — created_via='qr' only */}
            <Route path="/qr-dashboard" element={
              <RouteGuard allowedRoles={["fournisseur", "supplier_guest"]} redirectTo="/auth">
                <QrDashboard />
              </RouteGuard>
            } />

            {/* ===== SuperAdmin V2 (Lot 1) — squelette additif, /admin reste inchangé ===== */}
            <Route
              path="/admin/v2"
              element={
                <RouteGuard allowedRoles={GOVERNANCE_ROLES} redirectTo="/auth">
                  <AppShellAdminV2 />
                </RouteGuard>
              }
            >
              <Route index element={<AdminV2Cockpit />} />
              <Route
                path="magasins"
                element={
                  <AdminV2Placeholder
                    title="Magasins"
                    description="Vue 360 réseau : santé, équipes, adoption, RDV, alertes structurelles."
                    legacyHref="/admin"
                    legacyLabel="Voir SuperAdmin actuel"
                  />
                }
              />
              <Route
                path="fournisseurs"
                element={
                  <AdminV2Placeholder
                    title="Fournisseurs"
                    description="Pilotage adoption fournisseurs, sectorisation et visibilité."
                    legacyHref="/admin/supplier-visibility-shadow"
                    legacyLabel="Simulation fournisseur (legacy)"
                  />
                }
              />
              <Route
                path="entreprises"
                element={
                  <AdminV2Placeholder
                    title="Entreprises"
                    description="Vue agrégée des entreprises fournisseurs et leurs équipes."
                    legacyHref="/admin"
                    legacyLabel="Voir SuperAdmin actuel"
                  />
                }
              />
              <Route
                path="centrales"
                element={
                  <AdminV2Placeholder
                    title="Centrales"
                    description="Gestion des centrales d'achat, GT, points de scan et activation."
                    legacyHref="/admin/centrales"
                    legacyLabel="Centrales (legacy)"
                  />
                }
              />
              <Route
                path="rdv"
                element={
                  <AdminV2Placeholder
                    title="RDV"
                    description="Pilotage des rendez-vous : volumes, statuts, anomalies."
                    legacyHref="/admin/analyses"
                    legacyLabel="Analyses (legacy)"
                  />
                }
              />
              <Route
                path="qr-presences"
                element={
                  <AdminV2Placeholder
                    title="QR & Présences"
                    description="Couverture QR, check-ins, visiteurs et présences temps réel."
                    legacyHref="/admin"
                    legacyLabel="Voir SuperAdmin actuel"
                  />
                }
              />
              <Route
                path="invitations"
                element={
                  <AdminV2Placeholder
                    title="Invitations & activation"
                    description="Pipeline d'invitations, taux d'activation et relances."
                    legacyHref="/admin/audit-comptes"
                    legacyLabel="Audit comptes (legacy)"
                  />
                }
              />
              <Route
                path="qualite-donnees"
                element={
                  <AdminV2Placeholder
                    title="Qualité des données"
                    description="Alertes structurelles : magasins sans admin, profils orphelins, données manquantes."
                    legacyHref="/admin/v2-readiness"
                    legacyLabel="Pré-activation V2 (legacy)"
                  />
                }
              />
              <Route
                path="analyses"
                element={
                  <AdminV2Placeholder
                    title="Analyses"
                    description="Analyses transverses du réseau et performances par segment."
                    legacyHref="/admin/analyses"
                    legacyLabel="Analyses (legacy)"
                  />
                }
              />
              <Route
                path="observabilite"
                element={
                  <AdminV2Placeholder
                    title="Observabilité"
                    description="Cron runs, edge functions, erreurs, incidents et santé technique."
                    legacyHref="/admin/cron-runs"
                    legacyLabel="Cron runs (legacy)"
                  />
                }
              />
              <Route
                path="support"
                element={
                  <AdminV2Placeholder
                    title="Support"
                    description="Tickets support et demandes de contact."
                    legacyHref="/admin/support-tickets"
                    legacyLabel="Tickets support (legacy)"
                  />
                }
              />
              <Route
                path="gouvernance"
                element={
                  <AdminV2Placeholder
                    title="Gouvernance"
                    description="Fonctions métier, catalogue secteurs et règles transverses."
                    legacyHref="/admin/fonctions-metier"
                    legacyLabel="Fonctions métier (legacy)"
                  />
                }
              />
              <Route
                path="outils"
                element={
                  <AdminV2Placeholder
                    title="Outils"
                    description="Outils internes diagnostic et exploration (lecture seule)."
                    legacyHref="/admin/audit-comptes"
                    legacyLabel="Audit comptes (legacy)"
                  />
                }
              />
            </Route>

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <CookieConsentBanner />
        <PWAInstallPrompt />
        </DataConsentGate>
        </SuperAdminPreviewProvider>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
